import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import BACKEND_URL from '@/config/config';
import { clearQueryCache } from '@/config/queryClient';
import { errors, logger, metrics } from '@/observability';
import { router } from '@/router';
import {
  AUTH_STORAGE_KEYS,
  AUTH_UNAUTHORIZED_EVENT,
  type ILoginResponse,
  type TAuthHeaders,
} from '@/types/auth';
import { resolveXTenantIdHeader } from '@/utils/tenantScope';

const AUTH_SKIP_REFRESH = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
];

/** Endpoints que não usam EffectiveTenantId — não injeta X-Tenant-Id. */
const AUTH_SKIP_TENANT_HEADER = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/me',
  '/tenants',
];

let refreshPromise: Promise<string | null> | null = null;

function clearAuthStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.userData);
  localStorage.removeItem(AUTH_STORAGE_KEYS.effectiveTenantId);
}

function isLoginRoute(): boolean {
  const { pathname } = window.location;
  return pathname === '/login' || pathname === '/login/';
}

function isAuthSkipRefresh(url?: string): boolean {
  if (!url) return false;
  return AUTH_SKIP_REFRESH.some((endpoint) => url.includes(endpoint));
}

function shouldAttachTenantHeader(url?: string): boolean {
  if (!url) return true;
  return !AUTH_SKIP_TENANT_HEADER.some((endpoint) => url.includes(endpoint));
}

function handleUnauthorized(): void {
  clearAuthStorage();
  clearQueryCache();
  window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));

  if (isLoginRoute()) return;

  const redirect = `${window.location.pathname}${window.location.search}`;
  void router.navigate({
    to: '/login',
    search: { redirect },
  });
}

class ApiService {
  readonly baseURL: string;
  private readonly client: AxiosInstance;
  private readonly refreshClient: AxiosInstance;

  constructor() {
    // Usa config.ts (runtime-config.js no Docker > VITE_* bakeado no build).
    this.baseURL = BACKEND_URL;
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: { 'Content-Type': 'application/json' },
    });
    this.refreshClient = axios.create({
      baseURL: this.baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (shouldAttachTenantHeader(config.url)) {
        const tenantId = resolveXTenantIdHeader();
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId;
        }
      }

      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        if (!isAxiosError(error) || error.response?.status !== 401) {
          return Promise.reject(error instanceof Error ? error : new Error('Request failed'));
        }

        const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
        if (!original || original._retry || isAuthSkipRefresh(original.url)) {
          handleUnauthorized();
          return Promise.reject(error);
        }

        original._retry = true;
        const newAccess = await this.refreshAccessToken();
        if (!newAccess) {
          handleUnauthorized();
          return Promise.reject(error);
        }

        original.headers.Authorization = `Bearer ${newAccess}`;
        const tenantId = resolveXTenantIdHeader();
        if (tenantId && shouldAttachTenantHeader(original.url)) {
          original.headers['X-Tenant-Id'] = tenantId;
        }
        return this.client.request(original);
      },
    );
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (!refreshPromise) {
      refreshPromise = this.doRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  }

  private async doRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    if (!refreshToken) {
      return null;
    }

    try {
      const { data } = await this.refreshClient.post<ILoginResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      });
      if (!data.access_token || !data.refresh_token) {
        return null;
      }
      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, data.access_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, data.refresh_token);
      return data.access_token;
    } catch (error: unknown) {
      metrics.increment('auth.refresh_failed');
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401) {
          logger.warn('Token refresh rejected', { status: 401 });
          return null;
        }
        // Rede (sem response) ou 5xx — captura com tag; sem tokens nos logs.
        if (status == null || status >= 500) {
          errors.capture(error, { tags: { source: 'api.refresh' } });
        } else {
          logger.warn('Token refresh failed', { status });
        }
        return null;
      }
      errors.capture(error, { tags: { source: 'api.refresh' } });
      return null;
    }
  }

  getAuthHeaders(): TAuthHeaders {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    const base: TAuthHeaders = {
      'Content-Type': 'application/json',
    };
    if (token) {
      base.Authorization = `Bearer ${token}`;
    }
    const tenantId = resolveXTenantIdHeader();
    if (tenantId) {
      base['X-Tenant-Id'] = tenantId;
    }
    return base;
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /** POST que retorna blob (ex.: preview PNG). */
  async postBlob(url: string, data?: unknown): Promise<Blob> {
    const response = await this.client.post(url, data, { responseType: 'blob' });
    return response.data as Blob;
  }
}

const api = new ApiService();
export default api;
